import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, Subscription } from 'rxjs';
import { IUsersActivity, UsersActivityData } from '../../core/models/usersActivityData';
import { SocketGroup, SocketService } from '../../core/services/socket.service';
import {
	findPlanogramForActivityUpdate,
	isSameUsersActivity,
	mergePlanogramUsersActivity
} from './planogram-users-activity.util';

@Injectable()
export class PlanogramGroupUsersActivityService {

	private readonly socketService: SocketService = inject(SocketService);
	private readonly byPlanogramId: Map<number, Array<IUsersActivity>> = new Map();
	private captureQueue: Array<number> = [];
	private subscription: Subscription | null = null;
	readonly subject: BehaviorSubject<UsersActivityData> = new BehaviorSubject<UsersActivityData>(null);

	connect(planogramIds: Array<number>): BehaviorSubject<UsersActivityData> {
		this.disconnect();

		planogramIds.forEach((planogramId: number) => {
			this.socketService.addSubscription(SocketGroup.PlanogramUsersActivity, { planogram_id: planogramId });
		});

		if (planogramIds.length <= 1) return this.socketService.planogramUsersActivity;

		this.byPlanogramId.clear();
		this.captureQueue = [...planogramIds];
		this.subscription = this.socketService.planogramUsersActivity.subscribe(() => this.onSocketUpdate());

		return this.subject;
	}

	disconnect(planogramIds: Array<number> = []): void {
		this.subscription?.unsubscribe();
		this.subscription = null;
		this.byPlanogramId.clear();
		this.captureQueue = [];

		planogramIds.forEach((planogramId: number) => {
			this.socketService.deleteSubscription(SocketGroup.PlanogramUsersActivity, { planogram_id: planogramId });
		});
	}

	private onSocketUpdate(): void {
		const activity: Array<IUsersActivity> = this.socketService.planogramUsersActivity.value?.activity ?? [];

		if (this.captureQueue.length) {
			this.byPlanogramId.set(this.captureQueue.shift()!, activity);

			if (!this.captureQueue.length) {
				this.emitMerged();
			}

			return;
		}

		const planogramId: number | null = findPlanogramForActivityUpdate(this.byPlanogramId, activity);
		const cached: Array<IUsersActivity> | undefined = planogramId != null ? this.byPlanogramId.get(planogramId) : undefined;

		if (planogramId == null || (cached && isSameUsersActivity(cached, activity))) return;

		this.byPlanogramId.set(planogramId, activity);
		this.emitMerged();
	}

	private emitMerged(): void {
		this.subject.next({ activity: mergePlanogramUsersActivity(this.byPlanogramId) });
	}
}
